import { GoogleLogin as GoogleLoginBase, CredentialResponse } from '@react-oauth/google';
import type { ComponentProps, ComponentType } from 'react';
import { useRef, useEffect, useState } from 'react';

const GoogleLogin = GoogleLoginBase as ComponentType<ComponentProps<typeof GoogleLoginBase> & { disabled?: boolean }>;
import { useGoogleSignIn } from '@/hooks/useAccount';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useReferralTracker } from '@/hooks/useReferralTracker';
import { useTheme } from '@/components/theme-provider';
import { AUTH_RETURN_URL_KEY } from '@/constants/auth';
import { getSystemTheme, getLocalStorage, removeLocalStorage } from '@/lib/isomorphic';
import { trackEvent } from '@/lib/analytics';

interface GoogleSignInButtonProps {
  onLoginSuccess?: (data: { isNewAccount: boolean }) => void;
}

export function GoogleSignInButton({ onLoginSuccess }: GoogleSignInButtonProps) {
  const { t } = useTranslation('common');
  const googleSignInMutation = useGoogleSignIn();
  const navigate = useNavigate();
  const { getReferralId, clearReferralId } = useReferralTracker();
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && getSystemTheme() === 'dark');
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonWidth, setButtonWidth] = useState(320);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width;
      if (width) setButtonWidth(Math.floor(width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSuccess = async (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      try {
        const referredBy = getReferralId();
        const result = await googleSignInMutation.mutateAsync({
          idToken: credentialResponse.credential,
          referredBy: referredBy
        });
        if (referredBy) clearReferralId();

        trackEvent(result.created ? 'signup' : 'login', { method: 'google' });

        if (onLoginSuccess) {
          onLoginSuccess({ isNewAccount: result.created ?? false });
        } else {
          // Check for stored return URL
          const returnUrl = getLocalStorage(AUTH_RETURN_URL_KEY)
          if (returnUrl) {
            removeLocalStorage(AUTH_RETURN_URL_KEY)
            navigate({ to: returnUrl })
          } else {
            navigate({ to: '/dialer' });
          }
        }
      } catch (error) {
        console.error('Google Sign-In backend error:', error);
        toast.error(t('auth.googleLoginFailed'));
      }
    }
  };

  const handleError = () => {
    console.error('Google Sign-In failed or cancelled');
    toast.error(t('auth.googleLoginFailed'));
  };

  return (
    <div ref={containerRef} className="w-full overflow-hidden">
      <GoogleLogin
        onSuccess={handleSuccess}
        onError={handleError}
        text="continue_with"
        width={buttonWidth}
        type="standard"
        shape="pill"
        theme={isDark ? 'filled_black' : 'outline'}
        disabled={googleSignInMutation.isPending}
      />
    </div>
  );
}
